import React, { useEffect, useRef, useState } from 'react';

import {
    acknowledgeMaintenanceCompensation,
    MaintenanceCompensation,
} from '@/lib/api';


type Props = {
    compensation: MaintenanceCompensation | null;
    onAcknowledged: (result: MaintenanceCompensation) => void;
};


export const MaintenanceCompensationModal: React.FC<Props> = ({ compensation, onAcknowledged }) => {
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const buttonRef = useRef<HTMLButtonElement>(null);

    useEffect(() => {
        if (!compensation?.pending) return;
        const previousOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        buttonRef.current?.focus();
        return () => {
            document.body.style.overflow = previousOverflow;
        };
    }, [compensation?.event_id, compensation?.pending]);

    if (!compensation?.pending) return null;

    const acknowledge = async () => {
        if (submitting) return;
        setSubmitting(true);
        setError(null);
        try {
            const response = await acknowledgeMaintenanceCompensation();
            onAcknowledged(response.data);
        } catch (err) {
            console.error('maintenance compensation acknowledge failed', err);
            setError('確認の保存に失敗しました。通信状態をご確認のうえ、もう一度お試しください。');
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="maintenance-compensation-title"
            aria-describedby="maintenance-compensation-description"
            className="fixed inset-0 z-[140] flex items-center justify-center bg-slate-950/75 p-4 backdrop-blur-md"
        >
            <div className="w-full max-w-lg rounded-3xl border border-amber-300/70 bg-gradient-to-b from-amber-50 to-white p-6 text-slate-900 shadow-2xl sm:p-8">
                <div className="mb-2 text-sm font-bold tracking-[0.18em] text-amber-700">MAISON PORTAL</div>
                <h2 id="maintenance-compensation-title" className="mb-4 text-2xl font-extrabold leading-tight">
                    メンテナンスへのご協力、ありがとうございました
                </h2>
                <div id="maintenance-compensation-description" className="space-y-3 leading-7 text-slate-700">
                    <p>
                        メンテナンスの影響で投稿しづらい時間が発生し、ご不便をおかけしました。
                    </p>
                    <p>
                        直近1週間に発生した投稿未完了ペナルティはすべて取り消し、減点分
                        <strong className="mx-1 text-slate-950">{compensation.recent_penalty_refund} Toku</strong>
                        をお戻ししました。
                    </p>
                    {compensation.duplicate_penalty_refund > 0 && (
                        <p>
                            あわせて、過去に重複していた減点
                            <strong className="mx-1 text-slate-950">{compensation.duplicate_penalty_refund} Toku</strong>
                            も返還しました。
                        </p>
                    )}
                    <p>
                        復旧をお待ちいただいた感謝として、さらに
                        <strong className="mx-1 text-amber-700">{compensation.goodwill_bonus} Tokuポイント</strong>
                        をお贈りしました。すべて、すでに反映されています。
                    </p>
                </div>

                <div className="my-5 grid grid-cols-2 gap-3 rounded-2xl bg-amber-100/70 p-4 text-center">
                    <div>
                        <div className="text-xs font-semibold text-slate-600">今回の合計加算</div>
                        <div className="mt-1 text-xl font-black text-amber-800">+{compensation.total_credit} Toku</div>
                    </div>
                    <div>
                        <div className="text-xs font-semibold text-slate-600">現在のTokuポイント</div>
                        <div className="mt-1 text-xl font-black text-slate-950">{compensation.current_smile_score}</div>
                    </div>
                </div>

                <button
                    ref={buttonRef}
                    type="button"
                    disabled={submitting}
                    onClick={acknowledge}
                    className="w-full rounded-2xl border border-amber-700 bg-gradient-to-b from-amber-300 to-amber-500 px-4 py-3 font-extrabold text-amber-950 shadow-lg transition active:translate-y-px disabled:cursor-wait disabled:opacity-70"
                >
                    {submitting ? '確認を保存しています…' : '確認しました'}
                </button>
                {error && <p role="alert" className="mt-3 text-sm font-semibold text-red-700">{error}</p>}
            </div>
        </div>
    );
};


export default MaintenanceCompensationModal;
